import React, { useState, useEffect } from "react";
import "./style.scss";
import "../../../style/feature.scss";
import "../../../style/typography.scss";
import "../../../style/block.scss";
import "../../../style/buttons.scss";
import "../../../style/forms.scss";
import { useNavigate } from "react-router";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import { REGISTER_PARTICIPANTS_URL } from "../../api/apiRoutes";
import useFacility from "../../../utils/hooks/useFacility";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../store";
import { useTranslation } from "react-i18next";
import { clearErrors } from "../../store/alerts/alertsActions";
import { Child } from "../../models/ParticipantRegistration";
import EAProgressBar from "../shared/ProgressBar/EAProgressBar";
import { getDefaultLink } from "../../../utils/getDefaultLink";
import GuardianStep, { GuardianInputs } from "./GuardianStep";
import ChildrenStep from "./ChildrenStep";
import SuccessStep from "./SuccessStep";

const RegisterParticipants: React.FC = () => {
  const navigate = useNavigate();
  const { post } = useApiRequest();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const facilityIds = useFacility();
  const saleConfiguration = useSelector((state: State) => state.config.configuration);

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [guardianData, setGuardianData] = useState<GuardianInputs | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [editingChildIndex, setEditingChildIndex] = useState<number | null>(null);
  const [showChildForm, setShowChildForm] = useState(false);

  useEffect(() => {
    dispatch(clearErrors());
    //eslint-disable-next-line
  }, []);

  const handleGuardianSubmit = (data: GuardianInputs) => {
    setGuardianData(data);
    setCurrentStep(2);
    setShowChildForm(true);
  };

  const handleAddChild = (child: Child) => {
    if (editingChildIndex !== null) {
      // Editing existing child
      const updatedChildren = [...children];
      updatedChildren[editingChildIndex] = child;
      setChildren(updatedChildren);
      setEditingChildIndex(null);
    } else {
      // Adding new child
      setChildren([...children, child]);
    }
    setShowChildForm(false);
  };

  const handleEditChild = (index: number) => {
    setEditingChildIndex(index);
    setShowChildForm(true);
  };

  const handleDeleteChild = (index: number) => {
    const updatedChildren = children.filter((_, i) => i !== index);
    setChildren(updatedChildren);
    if (editingChildIndex === index) {
      setEditingChildIndex(null);
      setShowChildForm(false);
    }
  };

  const handleCancelChildForm = () => {
    setEditingChildIndex(null);
    setShowChildForm(false);
  };

  const handleShowChildForm = () => {
    setShowChildForm(true);
    setEditingChildIndex(null);
  };

  const handleRegisterParticipants = async () => {
    if (children.length === 0 || !guardianData) {
      return;
    }

    const registrationData = {
      email: guardianData.email,
      firstName: guardianData.firstName,
      lastName: guardianData.lastName,
      phone: guardianData.phone,
      // password: guardianData.password,
      childList: children,
    };

    const { isError } = await post(REGISTER_PARTICIPANTS_URL(facilityIds), registrationData);
    if (!isError) {
      setCurrentStep(3);
    }
  };

  const handleGoToShop = () => {
    const defaultLink = getDefaultLink(saleConfiguration.componentList);
    navigate(defaultLink);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  return (
    <div className="auth-page flex-center-center">
      <div className="auth-box shadow">
        {currentStep !== 3 && (
          <>
            <h1>{t("zarejestruj_uczestnikow")}</h1>
            <p className="auth-description">
              Nie trać czasu na rejestrowanie uczestników na miejscu, zrób to wcześniej online.
            </p>
            <EAProgressBar currentStep={currentStep} totalSteps={3} />
          </>
        )}

        {currentStep === 1 && <GuardianStep onSubmit={handleGuardianSubmit} />}

        {currentStep === 2 && guardianData && (
          <ChildrenStep
            children={children}
            editingChildIndex={editingChildIndex}
            showChildForm={showChildForm}
            onAddChild={handleAddChild}
            onEditChild={handleEditChild}
            onDeleteChild={handleDeleteChild}
            onCancelChildForm={handleCancelChildForm}
            onShowChildForm={handleShowChildForm}
            onBack={handleBack}
            onRegister={handleRegisterParticipants}
            newsletterAgreement={guardianData.newsletterAgreement}
          />
        )}

        {currentStep === 3 && <SuccessStep onGoToShop={handleGoToShop} />}
      </div>
    </div>
  );
};

export default RegisterParticipants;
